import { useEffect, useState } from 'react'

import { joinNames } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/jointAppointment'

type Args = {
  open: boolean
  // People the edit takes off the visit; the back end deletes their appointments.
  removedNames: string[]
  // Overrides the Single-tab text; the Series tab names people per visit.
  warning?: string
  // What an accepted answer covers; defaults to the names.
  key?: string
}

export type RemovalConfirm = {
  isOpen: boolean
  warning: string
  // True when Save must wait for the answer; opens the prompt.
  requestConfirm: () => boolean
  accept: (resume: () => void) => void
  cancel: () => void
  // What the confirm modal needs; resume re-runs the save past this prompt.
  modalProps: (resume: () => void) => RemovalModalProps
}

export type RemovalModalProps = {
  isOpen: boolean
  warning: string
  onClose: () => void
  onConfirm: () => void
}

const warningOf = (names: string[]): string =>
  `${joinNames(names)} will be taken off this visit and their ${
    names.length > 1 ? 'appointments' : 'appointment'
  } deleted, in OpenEMR too. Do you still want to continue?`

export const useRemovalConfirm = ({
  open,
  removedNames,
  warning: override,
  key: givenKey,
}: Args): RemovalConfirm => {
  const [isOpen, setIsOpen] = useState(false)
  const [acceptedKey, setAcceptedKey] = useState<string | null>(null)
  const key = givenKey ?? removedNames.join('|')

  useEffect(() => {
    setIsOpen(false)
    setAcceptedKey(null)
  }, [open])

  const warning = removedNames.length ? override ?? warningOf(removedNames) : ''
  const accept = (resume: () => void) => {
    setAcceptedKey(key)
    setIsOpen(false)
    resume()
  }
  const cancel = () => setIsOpen(false)

  return {
    isOpen,
    warning,
    requestConfirm: () => {
      if (!key || key === acceptedKey) return false
      setIsOpen(true)
      return true
    },
    accept,
    cancel,
    modalProps: (resume) => ({ isOpen, warning, onClose: cancel, onConfirm: () => accept(resume) }),
  }
}
